import { cn } from '@/lib/utils';
import Link from 'next/link';
import classes from './bannerHotspots.module.css';

// 后台存的是字体 token，和 Lovevox\Cms\Helper\Hotspot::FONTS 一一对应。
// 不映射就会继承页面字体（首页是 Paytone One，一个很重的展示体），按钮会糊成一坨。
const FONT_STACK = {
  'roboto-light': 'var(--font-roboto-light), Arial, sans-serif',
  'roboto-regular': 'var(--font-roboto-regular), Arial, sans-serif',
  'roboto-medium': 'var(--font-roboto-medium), Arial, sans-serif',
  teko: 'var(--font-teko), Arial, sans-serif',
};

const ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' };
const VALIGN = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };

const isArea = h => !h.type || h.type === 'area';

// 与后台编辑器 hotspot.phtml 的 zIndexOf 保持同一套规则：
// 热区按面积叠放（小的在上），按钮和标题始终盖在热区之上
const zIndexOf = (h, i) =>
  isArea(h) ? Math.max(1, Math.round(100 - (h.width * h.height) / 100)) : 200 + i;

// 后台把 px 存成了「占图片宽度的百分比」，这里用 cqw 还原：
// 1cqw = 容器（也就是图片）宽度的 1%，所以缩放时圆角和描边始终和设计稿同比例
const styleOf = (h, i) => {
  const bg = h.bg_color || 'transparent';
  const color = h.color || 'inherit';
  const border = h.border_color || 'transparent';
  const hasBorder = h.border_width_pct > 0 && h.border_color;

  return {
    left: `${h.left}%`,
    top: `${h.top}%`,
    width: `${h.width}%`,
    height: `${h.height}%`,
    zIndex: zIndexOf(h, i),
    borderStyle: hasBorder ? 'solid' : 'none',
    borderWidth: hasBorder ? `${h.border_width_pct}cqw` : 0,
    borderRadius: h.radius_pct ? `${h.radius_pct}cqw` : 0,
    fontSize: `${h.font_size}cqw`,
    fontWeight: h.font_weight || 400,
    fontFamily: FONT_STACK[h.font_family] || 'inherit',
    justifyContent: ALIGN[h.align] || 'center',
    alignItems: VALIGN[h.valign] || 'center',
    textAlign: h.align || 'center',
    '--hs-bg': bg,
    '--hs-color': color,
    '--hs-border': border,
    // hover 三色留空表示不变，回落到常态值
    '--hs-hover-bg': h.hover_bg_color || bg,
    '--hs-hover-color': h.hover_color || color,
    '--hs-hover-border': h.hover_border_color || border,
  };
};

/**
 * banner 图上的热区层。pc / mobile 各渲染一层，用 css 媒体查询切换，
 * 断点和 <picture> 里的 source 一致（640px）。
 */
const BannerHotspots = ({ hotspots, device }) => {
  if (!hotspots || !hotspots.length) {
    return null;
  }

  return (
    <div className={cn(classes.layer, device === 'mobile' ? classes.mobileOnly : classes.desktopOnly)}>
      {hotspots.map((h, i) => {
        const style = styleOf(h, i);
        const text = isArea(h) ? null : <span className={classes.text}>{h.text}</span>;

        // 没有链接的标题只是块文字，不拦截点击，让它下面的热区照样能点
        if (!h.link) {
          return (
            <span
              key={i}
              className={cn(classes.hotspot, classes.static)}
              style={style}
            >
              {text}
            </span>
          );
        }

        return (
          <Link
            key={i}
            href={h.link}
            className={classes.hotspot}
            style={style}
            aria-label={h.title || h.text || undefined}
          >
            {text}
          </Link>
        );
      })}
    </div>
  );
};

export default BannerHotspots;
